import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { backgroundsService } from '@/api/services/backgrounds.service'
import type { BackgroundsSizeFilter } from '@/lib/backgrounds/types/backgrounds-size-filter'
import type { BackgroundsTypeOption } from '@/lib/backgrounds/types/backgrounds-type-option'
import { resolveBackgroundsRequestCombos } from '@/lib/backgrounds/resolve-backgrounds-request-combos'
import { dedupeBackgroundsById } from '@/lib/backgrounds/dedupe-backgrounds'

/**
 * `doublePage`/`fourColor` are required booleans on the API (see `resolveBackgroundsRequestCombos`),
 * so an unconstrained Size ("any") or Type ("All backgrounds") fans out into multiple parallel
 * requests here rather than one — merged and de-duped into a single result list.
 */
export function useBackgroundsQuery(
  size: BackgroundsSizeFilter,
  type: BackgroundsTypeOption,
  options?: { enabled?: boolean }
) {
  const combos = useMemo(() => resolveBackgroundsRequestCombos(size, type), [size, type])

  const results = useQueries({
    queries: combos.map((combo) => ({
      queryKey: queryKeys.backgrounds.list(combo),
      queryFn: () => backgroundsService.getBackgrounds(combo),
      // Kept for the whole session (until a reload): reopening the Backgrounds panel with the same
      // Type/Size shows exactly the results it closed on, with no reload or reshuffle — only a
      // filter change fetches anything new. The library doesn't change mid-session in practice.
      staleTime: Infinity,
      gcTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
      enabled: options?.enabled ?? true,
    })),
  })

  const isLoading = results.some((query) => query.isLoading)
  const isError = results.some((query) => query.isError)
  const data = useMemo(
    () => dedupeBackgroundsById(results.flatMap((query) => query.data ?? [])),
    [results]
  )

  return { data, isLoading, isError }
}
